import React, { useState, useEffect } from 'react';
import { Server, Zap, Users, HeadphonesIcon, Shield, CheckCircle, ArrowRight, Send, Building2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ISPSolutions = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    current_subscribers: '',
    services_interested: [],
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get(`${API}/isp-packages`);
        setPackages(response.data);
      } catch (error) {
        console.error('Error fetching ISP packages:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/isp-inquiry`, formData);
      setIsSubmitted(true);
      toast.success('Inquiry submitted successfully! Our team will contact you soon.');
      setFormData({
        company_name: '',
        contact_person: '',
        email: '',
        phone: '',
        city: '',
        state: '',
        current_subscribers: '',
        services_interested: [],
        message: ''
      });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    { icon: Zap, title: 'Low Cost', description: 'Wholesale rates on 800+ channels for maximum margins' },
    { icon: Server, title: 'Complete Solution', description: 'IPTV middleware, EPG, and content delivery infrastructure' },
    { icon: Users, title: 'Scalable', description: 'From 100 to 100,000+ subscribers - we grow with you' },
    { icon: HeadphonesIcon, title: '24/7 Support', description: 'Dedicated technical support and account management' },
    { icon: Shield, title: 'TRAI Compliant', description: 'All channels and packages follow TRAI regulations' },
    { icon: Building2, title: 'White Label', description: 'Brand the service as your own with custom UI' }
  ];

  return (
    <div data-testid="isp-solutions-page">
      {/* Hero Section */}
      <section className="bg-[#0A0A0A] text-white py-16 md:py-24" data-testid="isp-hero">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="badge bg-red-500/20 text-red-400 mb-4">For Internet Service Providers</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6">
              Launch IPTV Services
              <span className="text-[#D92323] block">At Lowest Cost</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed mb-8">
              Partner with A Digital to offer IPTV services to your broadband subscribers. 
              Get access to 800+ channels, complete middleware solution, and 24/7 support.
            </p>
            <a href="#inquiry-form" className="btn-primary inline-flex items-center gap-2" data-testid="isp-cta-inquiry">
              Request Partnership
              <ArrowRight size={18} strokeWidth={2} />
            </a>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section bg-white" data-testid="isp-benefits">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="badge badge-default mb-4">Why Partner With Us</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Complete IPTV Solution
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-gray-50 rounded-2xl p-6 hover:bg-white hover:shadow-lg hover:border-[#D92323]/20 border border-transparent transition-all"
                data-testid={`benefit-${index}`}
              >
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon size={24} className="text-[#D92323]" strokeWidth={2} />
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="section bg-[#F9FAFB]" data-testid="isp-packages">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="badge badge-featured mb-4">ISP Partnership Plans</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Choose Your Package
            </h2>
            <p className="text-gray-600 mt-2">Flexible solutions for ISPs of all sizes</p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-96 bg-white rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {packages.map((pkg, index) => (
                <div 
                  key={pkg.id}
                  className={`bg-white rounded-2xl p-6 border-2 transition-all ${
                    pkg.popular 
                      ? 'border-[#D92323] shadow-xl scale-105 relative z-10' 
                      : 'border-gray-100 hover:border-[#D92323]/30'
                  }`}
                  data-testid={`isp-package-${pkg.id}`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-[#D92323] text-white px-4 py-1 rounded-full text-sm font-bold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <h3 className="font-bold text-xl text-gray-900 mb-2">{pkg.name}</h3>
                  <p className="text-gray-500 text-sm mb-4">{pkg.description}</p>
                  <div className="text-3xl font-bold text-[#D92323] font-mono-condensed mb-2">
                    {pkg.channels}
                  </div>
                  <p className="text-sm text-gray-500 mb-6">{pkg.price}</p>
                  <ul className="space-y-3 mb-6">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" strokeWidth={2} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a 
                    href="#inquiry-form" 
                    className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-full font-bold transition-colors ${
                      pkg.popular 
                        ? 'bg-[#D92323] text-white hover:bg-red-700' 
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    }`}
                  >
                    Get Started
                    <ArrowRight size={16} strokeWidth={2} />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Inquiry Form Section */}
      <section id="inquiry-form" className="section bg-white" data-testid="isp-inquiry-form">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                Partner With Us
              </h2>
              <p className="text-gray-600 mb-8">
                Fill out the form below and our ISP partnership team will contact you within 24 hours.
              </p>

              {isSubmitted ? (
                <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center" data-testid="isp-success-message">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={32} className="text-green-600" strokeWidth={2} />
                  </div>
                  <h3 className="text-xl font-bold text-green-800 mb-2">Inquiry Submitted!</h3>
                  <p className="text-green-700 mb-4">
                    Thank you for your interest. Our ISP partnership team will contact you within 24 hours.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-green-700 font-semibold hover:text-green-800 underline"
                  >
                    Submit another inquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="isp-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Company Name *</label>
                      <input
                        type="text"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D92323]/20 focus:border-[#D92323]"
                        placeholder="Your ISP Name"
                        data-testid="isp-company-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Person *</label>
                      <input
                        type="text"
                        name="contact_person"
                        value={formData.contact_person}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D92323]/20 focus:border-[#D92323]"
                        placeholder="Full Name"
                        data-testid="isp-contact-person"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D92323]/20 focus:border-[#D92323]"
                        placeholder="your@company.com"
                        data-testid="isp-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D92323]/20 focus:border-[#D92323]"
                        placeholder="+91 XXXXX XXXXX"
                        data-testid="isp-phone"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">City *</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D92323]/20 focus:border-[#D92323]"
                        placeholder="Your City"
                        data-testid="isp-city"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">State *</label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D92323]/20 focus:border-[#D92323]"
                        placeholder="Your State"
                        data-testid="isp-state"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Current Subscribers *</label>
                    <select
                      name="current_subscribers"
                      value={formData.current_subscribers}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D92323]/20 focus:border-[#D92323]"
                      data-testid="isp-subscribers"
                    >
                      <option value="">Select Range</option>
                      <option value="0-500">0 - 500</option>
                      <option value="500-1000">500 - 1,000</option>
                      <option value="1000-5000">1,000 - 5,000</option>
                      <option value="5000-10000">5,000 - 10,000</option>
                      <option value="10000+">10,000+</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Additional Requirements</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D92323]/20 focus:border-[#D92323] resize-none"
                      placeholder="Tell us about your requirements..."
                      data-testid="isp-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full md:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="isp-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={18} strokeWidth={2} />
                        Submit Inquiry
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div>
              <div className="bg-[#0A0A0A] text-white rounded-3xl p-8">
                <h3 className="text-xl font-bold mb-6">Direct Contact</h3>
                <p className="text-gray-400 mb-6">
                  Prefer to talk directly? Reach out to our ISP partnership team.
                </p>
                <div className="space-y-4">
                  <a href="tel:9019206699" className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors">
                    <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#D92323]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <span>9019206699</span>
                  </a>
                  <a href="mailto:isp@adigital.tv" className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors">
                    <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#D92323]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <span>isp@adigital.tv</span>
                  </a>
                </div>

                <hr className="border-gray-700 my-6" />

                <h4 className="font-bold mb-4">What You Get</h4>
                <ul className="space-y-3">
                  {[
                    '800+ Channels at wholesale rates',
                    'Complete IPTV middleware solution',
                    'Android & Linux STB support',
                    'EPG and content management',
                    'Technical training for your team',
                    '24/7 technical support'
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-2 text-gray-400">
                      <CheckCircle size={16} className="text-green-500" strokeWidth={2} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-r from-red-600 to-red-700">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Launch IPTV?
          </h2>
          <p className="text-red-100 mb-6">
            Join 50+ ISPs already partnering with A Digital
          </p>
          <a href="tel:9019206699" className="bg-white text-red-600 px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-colors inline-flex items-center gap-2">
            Call 9019206699
          </a>
        </div>
      </section>
    </div>
  );
};

export default ISPSolutions;
